import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { Send } from "lucide-react";

interface Message {
  id: string;
  sender_user_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
  sender_name?: string;
}

interface ShopMessageThreadProps {
  itemId: string;
  itemName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShopMessageThread({ itemId, itemName, open, onOpenChange }: ShopMessageThreadProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && itemId) {
      fetchMessages();
      
      // Mark messages as read
      markAsRead();

      // Set up realtime subscription
      const channel = supabase
        .channel(`shop_messages:${itemId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'shop_messages',
            filter: `shop_item_id=eq.${itemId}`
          },
          () => {
            fetchMessages();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [open, itemId]);

  const fetchMessages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('shop_messages')
      .select(`
        id,
        sender_user_id,
        message,
        is_read,
        created_at
      `)
      .eq('shop_item_id', itemId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      // Fetch sender names
      const messagesWithNames = await Promise.all(
        (data || []).map(async (msg) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, display_name')
            .eq('id', msg.sender_user_id)
            .single();

          return {
            ...msg,
            sender_name: profile?.display_name || profile?.full_name || 'Unknown'
          };
        })
      );
      setMessages(messagesWithNames);
    }
    setLoading(false);
  };

  const markAsRead = async () => {
    if (!user) return;

    await supabase
      .from('shop_messages')
      .update({ is_read: true })
      .eq('shop_item_id', itemId)
      .neq('sender_user_id', user.id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !user) return;

    if (newMessage.length > 500) {
      toast({
        title: "Message too long",
        description: "Maximum 500 characters allowed",
        variant: "destructive"
      });
      return;
    }

    setSending(true);

    const { error } = await supabase
      .from('shop_messages')
      .insert({
        shop_item_id: itemId,
        sender_user_id: user.id,
        message: newMessage.trim()
      });

    if (error) {
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setNewMessage("");
      fetchMessages();
    }

    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Messages: {itemName}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-96 pr-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No messages yet. Start the conversation!
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] rounded-lg p-3 ${
                    msg.sender_user_id === user?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}>
                    <div className="text-xs font-semibold mb-1">{msg.sender_name}</div>
                    <div className="text-sm">{msg.message}</div>
                    <div className="text-xs opacity-70 mt-1">
                      {new Date(msg.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="flex gap-2 pt-4 border-t">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message... (max 500 characters)"
            className="min-h-[80px]"
            maxLength={500}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            size="icon"
            className="h-[80px] w-[80px]"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          {newMessage.length}/500
        </div>
      </DialogContent>
    </Dialog>
  );
}
